import { HotelCalendarType } from "@website/accommodation-ui/hotel-calendar";
import { calculateSnappProDisplayPricing } from "@website/accommodation-ui/hotel-card-new/hotel-card-action/hotel-card-pricing/hotel-card-pricing.utils";
import { HotelReserveBookingCardType } from "@website/accommodation-ui/hotel-reserve-booking-card";
import { HotelReserveDetailsPropertiesTypes } from "@website/accommodation-ui/hotel-reserve-details";
import { CancellationPlanType, CancellationRulesImportance, villaReserveCancellationPolicyType } from "@website/accommodation-ui/villa-reserve-cancellation-policies";
import { useFetcher } from "@website/fetcher/use-fetcher";
import { useLoginStatus } from "@website/hooks/auth-manager";
import { MaterialNameType } from "@website/icons/material";
import { BreadcrumbItemType, BreadcrumbPropertiesType } from "@website/ui/breadcrumb";
import { useSnackbar } from "@website/ui/snackbar";
import { getOrigin } from "@website/utils/channel/get-origin";
import { daysBetweenDates, isInvalidCheckin, today, tomorrow, transformDate } from "@website/utils/date-format";
import { numberConvert } from "@website/utils/number-convert";
import { numberSeparator } from "@website/utils/number-separator";
import { ReserveFacilitiesPropertiesTypes } from "@website/widget/reserve-facilities";
import { ReserveMapPropertiesTypes } from "@website/widget/reserve-map";
import { ReservePoliciesPropertiesTypes } from "@website/widget/reserve-policies";
import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";
import omit from "lodash-es/omit";
import pick from "lodash-es/pick";
import { useRouter } from "next/router";
import { MouseEvent, useEffect, useState } from "react";
import { ENDPOINTS } from "../../../constants";
import HamraheAval from "./assets/MCI.png";
import IrancellLogo from "./assets/irancel.png";
import Raitel from "./assets/rightel.png";
import { useBooking } from "./booking.hook";
import { useCalendar } from "./calendar.hook";
import { useHotelComments } from "./comments.hook";
import { fetchCalendarData, fetchInstalmentWallet, fetchPropertyPrice, fetchRoomInfo, fetchUsefulnessReviewData } from "./index.server";
import { handleFacilicyModalScrollChanged, useRoomPageTracking } from "./index.tracking";
import { HotelCalendarPropertiesType, HotelPagePropertiesType, RoomPropertyResponseType } from "./index.types";
export const useHotelPageDataTransform = ({
  roomInfo,
  seoData,
  hotelRateData
}: HotelPagePropertiesType) => {
  const router = useRouter();
  const {
    enqueue
  } = useSnackbar();
  const {
    isLoggedIn
  } = useLoginStatus();
  const {
    onDatepickerChange,
    onFlatCalendarChange,
    onCalendarAccept,
    calendarDate
  } = useCalendar();
  const passengersCount = Number(get(router.query, "room_capacity") ?? 0);
  const {
    data: freshRoomInfo
  } = useFetcher({
    enabled: Boolean(router.query["roomId"]),
    queryKey: ["roomInfo", router.query.roomId],
    placeholderData: roomInfo,
    queryFn: () => fetchRoomInfo(router.query)
  });
  const maximumAllowedPassengerCount = (freshRoomInfo?.extra_capacity || 0) + (freshRoomInfo?.capacity || 0);
  const isPassengerLimitExceeded = passengersCount > maximumAllowedPassengerCount;
  const {
    data: roomProperty,
    isFetching
  } = useFetcher({
    enabled: Boolean(router.query["roomId"]),
    queryKey: ["roomsProperty", JSON.stringify(router.query), router?.query?.["date_from"], router?.query?.["date_to"]],
    queryFn: () => fetchPropertyPrice(router.query).then(data => data as RoomPropertyResponseType)
  });
  const {
    price: displayTotalPrice,
    priceOff: displayTotalPriceOff = 0,
    snappProDiscountPercentage
  } = calculateSnappProDisplayPricing({
    price: roomProperty?.total_price || 0,
    priceOff: roomProperty?.total_local_price_off || 0,
    adjustments: roomProperty?.adjustments
  });
  const displayDiscount = roomProperty?.adjustments ? Math.max(0, displayTotalPrice - displayTotalPriceOff) : roomProperty?.discount || 0;
  const {
    handleTrackReserveButtonClicked
  } = useRoomPageTracking(freshRoomInfo, roomProperty);
  const [reviewIds, setReviewIds] = useState([]);
  const [hasError, setHasError] = useState(false);
  const onUsefulnessClick = (id: number) => setReviewIds(previousState => [...previousState, {
    id,
    isFetched: isFetched && !hasError
  }]);
  const {
    isFetched
  } = useFetcher({
    enabled: reviewIds.length > 0 && isLoggedIn,
    queryKey: ["usefulnessReview", reviewIds?.[reviewIds?.length - 1]?.id],
    queryFn: ({
      queryKey
    }) => fetchUsefulnessReviewData(Number(queryKey[1])).then(() => setHasError(false)).catch(error => {
      setHasError(true);
      enqueue(error?.response?.message, "error", "one-line");
    })
  });
  const {
    data: calendarAvailabilityData
  } = useFetcher<HotelCalendarPropertiesType>({
    queryFn: () => fetchCalendarData(router.query.roomId),
    enabled: Boolean(router.query.roomId),
    queryKey: ["calendarData", router.query.roomId]
  });
  const {
    comments,
    onLoadMore,
    onLoadFilter,
    filters,
    isLoading
  } = useHotelComments({
    hotelId: freshRoomInfo?.property?.id,
    roomId: freshRoomInfo?.id
  });
  const hotelReserveRateProperties = {
    ...hotelRateData,
    size: "medium" as const,
    hotelType: "اقامتگاه"
  };
  const {
    isLoading: requestBookingLoading,
    ...booking
  } = useBooking({
    propertyID: freshRoomInfo?.property?.id,
    min_stay: roomProperty?.min_stay,
    handleTrackReserveButtonClicked
  });
  const extraCapacity = Number(router.query.extra_capacity);
  const onChangePassengersCount = (value: number) => {
    const extra_capacity = value > freshRoomInfo?.capacity ? value - freshRoomInfo?.capacity : 0;
    router.replace({
      pathname: router.pathname,
      query: {
        ...router.query,
        room_capacity: value,
        extra_capacity
      }
    }, undefined, {
      shallow: true,
      scroll: false
    });
  };
  const disableDates = Object?.entries(calendarAvailabilityData?.calender || []).filter(value_0 => value_0[1]?.availability === 0 || !value_0[1]?.price || value_0[1].price === 0).map(([date]) => date);
  const {
    date_from: dateFrom,
    date_to: dateTo
  } = router.query;
  const isDisabledDateSelected = disableDates.some(date_0 => date_0 >= dateFrom && date_0 <= dateTo && date_0 !== dateTo);
  const shouldResetDateRange = isDisabledDateSelected || isInvalidCheckin(dateFrom, dateTo);
  useEffect(() => {
    if (!shouldResetDateRange) return;
    const defaultDateFrom = today();
    const defaultDateTo = tomorrow();
    if (dateFrom === defaultDateFrom && dateTo === defaultDateTo) return;
    router.replace({
      pathname: router.pathname,
      query: {
        ...router.query,
        date_from: defaultDateFrom,
        date_to: defaultDateTo
      }
    }, undefined, {
      shallow: true,
      scroll: false
    });
  }, [dateFrom, dateTo, router, shouldResetDateRange]);
  const lastDate = Object?.entries(calendarAvailabilityData?.calender || [])?.at(-1)?.[0];
  const disableLimit = {
    disableAfterDate: lastDate ? transformDate(new Date(lastDate as unknown as string)) : undefined
  };
  const {
    data: instalmentWallet,
    isLoading: isInstallmentWalletLoading,
    isFetching: isInstallmentWalletFetching
  } = useFetcher({
    enabled: isLoggedIn,
    retry: false,
    queryKey: ["instalmentWallet"],
    queryFn: () => fetchInstalmentWallet()
  });
  const snappPayData = {
    isEligible: instalmentWallet?.isEligible,
    showDownPayment: instalmentWallet?.showDownPayment,
    markup: instalmentWallet?.markup,
    isLoading: isInstallmentWalletLoading || isInstallmentWalletFetching
  };
  const hotelBreadcrumbsProperties = {
    color: "default" as const,
    variant: "default" as const,
    list: [{
      title: "ویلا و اقامتگاه",
      href: `${getOrigin()}/villa`
    }, {
      title: `ویلاهای استان ${freshRoomInfo?.property?.state?.title}`,
      href: {
        pathname: "/[locationType]/[cityTitle]",
        query: {
          ...pick(router.query, ["cityTitle", "locationType"]),
          locationType: "state",
          cityTitle: freshRoomInfo?.property?.state?.slug
        }
      },
      onClick: (event: MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        return router.push({
          pathname: "/[locationType]/[cityTitle]",
          query: {
            ...omit(router.query, "roomId"),
            locationType: "state",
            cityTitle: freshRoomInfo?.property?.state?.slug
          }
        }, undefined, {
          shallow: true,
          scroll: false
        });
      }
    }, {
      title: ` ویلاهای ${freshRoomInfo?.property?.city?.title}`,
      href: {
        pathname: "/[locationType]/[cityTitle]",
        query: {
          ...pick(router.query, ["cityTitle", "locationType"]),
          locationType: "city",
          cityTitle: freshRoomInfo?.property?.city?.slug
        }
      },
      onClick: (event_0: MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event_0.preventDefault();
        return router.push({
          pathname: "/[locationType]/[cityTitle]",
          query: {
            ...omit(router.query, "roomId"),
            locationType: "city",
            cityTitle: freshRoomInfo?.property?.city?.slug
          }
        }, undefined, {
          shallow: true,
          scroll: false
        });
      }
    }, {
      title: `${freshRoomInfo?.property?.title}`,
      href: {
        pathname: "/room/[roomId]",
        query: pick(router.query, ["roomId"])
      }
    }] as Array<BreadcrumbItemType>
  } as BreadcrumbPropertiesType;
  const transferredCalenderData = calendarAvailabilityData?.calender ? Object.fromEntries(Object.entries(calendarAvailabilityData?.calender).map(([key, value_1]) => {
    const {
      price,
      adjustments
    } = value_1;
    const discountWithoutSnappPro = adjustments.total - adjustments.snapp_pro;
    const hasDiscountWithoutSnappPro = discountWithoutSnappPro < 0;
    const convertedPrice = price ? numberConvert(numberSeparator(price / 1000), {
      locale: router.locale,
      numberOnly: false
    }) : 0;
    const priceOff = price ? numberConvert(numberSeparator((price + discountWithoutSnappPro) / 1000), {
      locale: router.locale,
      numberOnly: false
    }) : 0;
    return [key, {
      ...value_1,
      content: hasDiscountWithoutSnappPro ? <div className="flex flex-col items-center">
                    <span>{priceOff}</span>
                    <span className="line-through opacity-75">
                      {convertedPrice}
                    </span>
                  </div> : convertedPrice
    }];
  })) : {};
  const peakDates = Object?.entries(calendarAvailabilityData?.calender || []).filter(value_2 => value_2[1]?.dateType === "HighDemand").map(([date_1]) => date_1);
  const newYearDates = Object?.entries(calendarAvailabilityData?.calender || []).filter(value_3 => value_3[1]?.dateType === "Nowruz").map(([date_2]) => date_2);
  const cancellationRuleHintText = (() => {
    const isSelectedDateInNewYearTime = newYearDates.some(d => d >= router.query.date_from && d <= router.query.date_to);
    if (isSelectedDateInNewYearTime) {
      return "مقرارت لغو رزرو در این تاریخ برابر با مقرارت ایام نوروز است.";
    }
    const isSelectedDateInPeakTime = peakDates.some(d_0 => d_0 >= router.query.date_from && d_0 <= router.query.date_to);
    if (isSelectedDateInPeakTime) {
      return "مقرارت لغو رزرو در این تاریخ برابر با مقرارت پیک است.";
    }
    return "";
  })();
  const policies = [{
    type: CancellationRulesImportance.NORMAL,
    title: "روزهای عادی",
    text: freshRoomInfo?.property?.cancellation_rules_normal
  }, {
    type: CancellationRulesImportance.PEAK_TIME,
    title: "روزهای پیک",
    text: freshRoomInfo?.property?.cancellation_rules_high_demand
  }, {
    type: CancellationRulesImportance.NEW_YEAR,
    title: "ایام نوروز",
    text: freshRoomInfo?.property?.cancellation_rules_nowruz
  }];
  const ceilTimeRange = (time?: string) => {
    if (!time) return "";
    if (time.includes("-")) {
      const [start, end] = time.split("-");
      const ceilHour = t => {
        if (t === "infinite") return "infinite";
        const [h, m] = t.split(":").map(Number);
        return `${(h + (m > 0 ? 1 : 0)).toString().padStart(2, "0")}:00`;
      };
      return `${ceilHour(start)}-${ceilHour(end)}`;
    }
    const [h_0, m_0] = time.split(":").map(Number);
    return `${(h_0 + (m_0 > 0 ? 1 : 0)).toString().padStart(2, "0")}:00`;
  };
  return {
    calendarProps: {
      onCalendarChange: onFlatCalendarChange,
      calendarDate,
      isLabelTop: true,
      calendarData: transferredCalenderData,
      disableDates,
      disableLimit
    } as HotelCalendarType,
    gallery: (freshRoomInfo?.gallery || [])?.map(({
      title,
      name
    }, index) => ({
      id: index + 1,
      title,
      alt: freshRoomInfo?.title || freshRoomInfo?.property?.title || "",
      media: `${ENDPOINTS.CDN_IMAGES}${name}`
    })),
    booking: {
      ...booking,
      cancellationRuleHintText,
      onChangeDatePicker: onDatepickerChange,
      onCalendarAccept: onCalendarAccept,
      calendarDate,
      discount: displayDiscount,
      snappProDiscountPercentage,
      snappPayData,
      isInstant: roomProperty?.instant || false,
      isLoading: isFetching || requestBookingLoading,
      price: displayTotalPriceOff - (roomProperty?.total_extra_bed_price || 0),
      minStay: roomProperty?.min_stay,
      passengersCount,
      capacity: freshRoomInfo?.capacity || 0,
      extraCapacity,
      priceOfExtraPassengers: roomProperty?.total_extra_bed_price || 0,
      pricePerNight: roomProperty?.from ? roomProperty?.daily_price?.[roomProperty?.from]?.sellPrice || 0 : 0,
      total_price: displayTotalPrice,
      total_price_off: displayTotalPriceOff,
      basePrice: roomProperty?.base_price || 0,
      nightCount: daysBetweenDates(roomProperty?.from, roomProperty?.to) || 1,
      increaseDisabled: passengersCount >= (roomProperty?.extra_bed > 0 ? freshRoomInfo?.extra_capacity : 0) + freshRoomInfo?.capacity,
      decreaseDisabled: passengersCount <= 1,
      submitDisabled: isPassengerLimitExceeded || isEmpty(roomProperty) || isEmpty(roomProperty?.daily_price),
      submitDisabledMessage: isPassengerLimitExceeded || router?.query["date_from"] && router?.query["date_to"] && isEmpty(roomProperty) ? "امکان رزرو اقامتگاه در تاریخ و تعداد مسافر مشخص شده، وجود ندارد" : "",
      disableLimit: {
        disableAfterDate: lastDate ? transformDate(new Date(lastDate as unknown as string)) : undefined
      },
      onChangePassengersCount,
      disableDates,
      calendarData: transferredCalenderData
    } as HotelReserveBookingCardType,
    hotelReserveCancellationPoliciesProps: {
      policies,
      cancellationPlanType: freshRoomInfo?.property?.cancellation_plan || CancellationPlanType.UNKNOWN
    } as villaReserveCancellationPolicyType,
    hotelReservePoliciesProps: {
      checkinTime: numberConvert(ceilTimeRange(freshRoomInfo?.property?.check_in_time), {
        locale: router?.locale
      }),
      checkoutTime: numberConvert(ceilTimeRange(freshRoomInfo?.property?.check_out_time), {
        locale: router?.locale
      }),
      hotelType: "اقامتگاه",
      rulesAllowed: freshRoomInfo?.property?.rules_allowed,
      rulesDisAllowed: freshRoomInfo?.property?.rules_disallowed
    } as ReservePoliciesPropertiesTypes,
    hotelReserveHostPoliciesProps: {
      rules: freshRoomInfo?.property?.host_rule
    },
    hotelBreadcrumbsProps: hotelBreadcrumbsProperties,
    hotelReserveFacilitiesProps: {
      facilities: isEmpty(freshRoomInfo?.amenities) ? freshRoomInfo.property.amenities : freshRoomInfo?.amenities,
      facilitiesGroup: isEmpty(freshRoomInfo.amenitiesGroup) ? freshRoomInfo.property.amenitiesGroup : freshRoomInfo.amenitiesGroup,
      hotelTitle: freshRoomInfo?.title,
      hotelType: "اقامتگاه",
      onModalScrollChange: (value_4: number) => handleFacilicyModalScrollChanged({
        value: Number.parseInt(`${value_4}`)
      })
    } as ReserveFacilitiesPropertiesTypes,
    villaSimilarRoomsProps: {
      availability: roomProperty?.availability
    },
    hotelReserveDetailsProps: {
      id: freshRoomInfo.id,
      hotelTitle: seoData?.pageTitle || freshRoomInfo.title || "",
      address: [freshRoomInfo?.property?.state?.title, freshRoomInfo?.property?.city?.title, freshRoomInfo?.property?.village?.title]?.filter(Boolean)?.join(", ")
    } as HotelReserveDetailsPropertiesTypes,
    villaSpecifications: [...(freshRoomInfo?.summaries?.info ? [{
      iconName: "house" as MaterialNameType,
      title: "فضای اقامتگاه",
      description: numberConvert(freshRoomInfo?.summaries?.info, {
        locale: router?.locale
      })
    }] : []), ...(freshRoomInfo?.capacity ? [{
      iconName: "group" as MaterialNameType,
      title: "ظرفیت",
      description: `${freshRoomInfo?.capacity} نفر پایه ${freshRoomInfo?.extra_capacity ? `+ ${freshRoomInfo?.extra_capacity} نفر اضافه` : ""}`
    }, ...(roomProperty?.instant ? [{
      iconName: "bolt" as MaterialNameType,
      title: "رزرو آنی",
      description: "بدون نیاز به تایید میزبان"
    }] : []), ...(roomProperty?.extra_guest_base_price ? [{
      iconName: "person_add_alt_1" as MaterialNameType,
      title: "هزینه نفر اضافه",
      description: `${numberConvert(numberSeparator(roomProperty?.extra_guest_base_price))} تومان`
    }] : []), ...(freshRoomInfo?.summaries?.bedding ? [{
      iconName: "bed" as MaterialNameType,
      title: "سرویس خواب",
      description: numberConvert(freshRoomInfo?.summaries?.bedding, {
        locale: router?.locale
      })
    }] : []), ...(freshRoomInfo?.summaries?.bathroom ? [{
      iconName: "shower" as MaterialNameType,
      title: "سرویس بهداشتی",
      description: numberConvert(freshRoomInfo?.summaries?.bathroom, {
        locale: router?.locale
      })
    }] : [])] : [])],
    villaSpaces: freshRoomInfo?.spaces?.map(space => ({
      id: space?.id,
      title: space?.name,
      amenitiesGroup: Object?.entries(space?.amenitiesGroup)?.map(item => ({
        title: item?.[0],
        items: item?.[1]?.map(({
          title: title_0,
          description
        }) => ({
          title: title_0,
          description
        }))
      }))
    })),
    hotelReserveBtsCoveragesProps: freshRoomInfo?.property?.btsCoverage?.map(item_0 => ({
      ...item_0,
      ...(item_0?.cellOperator?.includes("ایرانسل") ? {
        logo: IrancellLogo?.src
      } : item_0?.cellOperator?.includes("اول") ? {
        logo: HamraheAval?.src
      } : item_0?.cellOperator?.includes("رایتل") ? {
        logo: Raitel?.src
      } : {})
    })) ?? [],
    hotelReserveRateProps: {
      ...hotelReserveRateProperties,
      pageTitle: seoData?.title || roomInfo?.title || freshRoomInfo?.title,
      pageDescription: seoData?.description || seoData?.title || roomInfo?.title || freshRoomInfo?.title
    },
    hotelReserveCommentsProps: {
      comments: comments.map(item_1 => ({
        ...item_1,
        helpfulSubmitted: !isLoggedIn || item_1?.helpfulSubmitted,
        submitSucceed: !isLoggedIn || reviewIds?.[reviewIds?.length - 1]?.id === item_1?.id ? isFetched : reviewIds?.filter(({
          id: id_0
        }) => id_0 === item_1?.id)?.[0]?.isFetched,
        onUsefulnessClick
      })),
      rate: hotelReserveRateProperties,
      hotelTitle: freshRoomInfo?.title,
      onLoadMore,
      isLoading,
      onLoadFilter,
      filters
    },
    hotelReserveMapProps: freshRoomInfo?.property?.location?.longitude && freshRoomInfo?.property?.location?.latitude ? {
      title: freshRoomInfo?.property?.title,
      type: "اقامتگاه",
      mapOptions: {
        center: {
          lng: freshRoomInfo?.property?.location?.longitude,
          lat: freshRoomInfo?.property?.location?.latitude
        }
      },
      circle: {
        lng: freshRoomInfo?.property?.location?.longitude,
        lat: freshRoomInfo?.property?.location?.latitude
      }
    } as ReserveMapPropertiesTypes : null,
    hotelReserveNearByProps: freshRoomInfo?.property,
    description: freshRoomInfo?.property?.description,
    isAnyPolicyExist: policies.some(s => Boolean(s?.text))
  };
};